import React, { useState } from 'react';

import PropTypes from 'prop-types';

import { Spinner } from '@/components/common';
import FullscreenSpinner from '@/components/common/FullscreenSpinner';
import Section from '@/components/Page/Section';
import DateInfo from '@/components/SchedulePage/DateInfo';
import DateSwitch from '@/components/SchedulePage/DateSwitch';
import OrderModal from '@/components/SchedulePage/OrderModal';
import ScheduleMap from '@/components/SchedulePage/ScheduleMap';
import Timeline from '@/components/SchedulePage/Timeline';
import PendingOrders from '@/components/SchedulePage/Timeline/PendingOrders';
import ViewSwitch from '@/components/SchedulePage/ViewSwitch';
import { formatCredits } from '@/components/SchedulePage/helpers/credits';
import creditsIcon from '@/images/icons/credits.svg';
import sendToDevicesIcon from '@/images/icons/send-to-devices.svg';
import classes from '@/components/SchedulePage/style.module.scss';

const SchedulePageComponent = ({
  selectedOrder,
  totalScheduledCredits,
  handleSubmit,
  isBusy,
  isButtonDisabled,
  orderAlreadyBeingEditedByAnotherUser,
  isUnsaved,
  isDisconnected,
  intl,
  sidebarCollapsed,
  activeView,
  hasOpenedMap,
  onViewChange,
  ordersLoading,
  inspectorsLoading,
  pendingOrdersLoading,
  pendingOrdersError,
}) => {
  const [mapState, setMapState] = useState({
    selectedMapOrderId: null,
    unlocatedOrderIds: [],
    onPendingOrderClick: null,
  });

  return (
    <Section padding="px-[1em] pt-[1.5em] pb-0" overflow="hidden">
      {isDisconnected && (
        <div className={`${classes.pageDisabled} ${sidebarCollapsed ? classes.sidebarCollapsed : ''}`}>
          <div
            className={`${classes.draftBorder} ${classes.isTop} ${sidebarCollapsed ? classes.sidebarCollapsed : ''}`}
          >
            Verifique a sua conexão com a internet. Tentando se reconectar...
          </div>
        </div>
      )}

      {isUnsaved && (
        <div className={`${classes.draftBorder} ${classes.isTop} ${sidebarCollapsed ? classes.sidebarCollapsed : ''}`}>
          Alguns pedidos ainda não foram enviados aos dispositivos.
        </div>
      )}

      {isBusy && (
        <div className={classes.busy}>
          <FullscreenSpinner text={intl.formatMessage({ id: 'WAIT_THE_ORDER_ARE_SENT_TO_INSPECTORS_MESSAGE' })} />
        </div>
      )}

      <div className={classes.top}>
        <div className={classes.dateContainer}>
          <ViewSwitch activeView={activeView} onChange={onViewChange} />
          <DateInfo />
          <DateSwitch />
        </div>

        <div className={classes.action}>
          <div className={classes.scheduledCredits}>
            <span>{intl.formatMessage({ id: 'SCHEDULED_CREDITS' })}</span>
            <img src={creditsIcon} alt="" />
            <strong>{formatCredits(totalScheduledCredits)}</strong>
          </div>
          <div className={classes.actionDivider} aria-hidden="true" />
          <button type="button" onClick={handleSubmit} disabled={isButtonDisabled}>
            {isButtonDisabled ? (
              <div className={classes.spinnerWrapper}>
                <Spinner color="inherit" />
              </div>
            ) : (
              <>
                <img src={sendToDevicesIcon} alt="Ícone flecha" />
                {intl.formatMessage({ id: 'SEND_TO_DEVICES' })}
              </>
            )}
          </button>
        </div>
      </div>

      <div className={`${classes.scheduleMainRow} ${activeView === 'map' ? classes.mapLayout : ''}`}>
        <PendingOrders
          busy={pendingOrdersLoading}
          error={pendingOrdersError}
          layout={activeView}
          selectedMapOrderId={mapState.selectedMapOrderId}
          unlocatedOrderIds={mapState.unlocatedOrderIds}
          onMapOrderClick={activeView === 'map' ? mapState.onPendingOrderClick : null}
        />

        <div className={classes.rightViewsArea}>
          <div
            className={activeView === 'schedule' ? classes.activeView : classes.hiddenView}
            aria-hidden={activeView !== 'schedule'}
          >
            <Timeline ordersLoading={ordersLoading} inspectorsLoading={inspectorsLoading} />
          </div>

          {hasOpenedMap && (
            <div
              className={activeView === 'map' ? classes.activeView : classes.hiddenView}
              aria-hidden={activeView !== 'map'}
            >
              <ScheduleMap isVisible={activeView === 'map'} onMapStateChange={setMapState} />
            </div>
          )}
        </div>
      </div>

      {selectedOrder && (
        <OrderModal
          orderId={selectedOrder.orderId}
          inspectorId={selectedOrder.inspectorId}
          startTime={selectedOrder.startTime}
          orderAlreadyBeingEditedByAnotherUser={orderAlreadyBeingEditedByAnotherUser}
        />
      )}
    </Section>
  );
};

SchedulePageComponent.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  isBusy: PropTypes.bool.isRequired,
  isButtonDisabled: PropTypes.bool.isRequired,
  isDisconnected: PropTypes.bool.isRequired,
  selectedOrder: PropTypes.object,
  totalScheduledCredits: PropTypes.number.isRequired,
  intl: PropTypes.object.isRequired,
  orderAlreadyBeingEditedByAnotherUser: PropTypes.object,
  isUnsaved: PropTypes.bool,
  sidebarCollapsed: PropTypes.bool.isRequired,
  activeView: PropTypes.oneOf(['schedule', 'map']).isRequired,
  hasOpenedMap: PropTypes.bool.isRequired,
  onViewChange: PropTypes.func.isRequired,
  ordersLoading: PropTypes.bool.isRequired,
  inspectorsLoading: PropTypes.bool.isRequired,
  pendingOrdersLoading: PropTypes.bool.isRequired,
  pendingOrdersError: PropTypes.bool.isRequired,
};

SchedulePageComponent.defaultProps = {
  selectedOrder: null,
  orderAlreadyBeingEditedByAnotherUser: undefined,
  isUnsaved: false,
};

export default SchedulePageComponent;
